"use client"

import { useCallback, useEffect, useState } from "react"
import { Container, Card, Button, Table, Form, Modal, Alert, Spinner } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchBuildings,
  addBuilding,
  updateBuilding,
  archiveBuilding,
  unarchiveBuilding,
} from "../../../redux/slices/buildingsSlice"
import { createBuildingChat } from "../../../redux/slices/messagesSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Edit, Archive, RefreshCw, Users } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { PlusButton } from "../../../components/ui/plus-button"
import "../../../styles/admin-buttons.css"
import {
  fetchSoftoneBuildingLinks,
  saveSoftoneBuildingLink,
  linkToSelectValue,
  selectValueToChoice,
} from "../../../api/softone"
import type { SoftoneBuilding, SoftoneBuildingLinkItem } from "../../../data/softone"
import { SoftoneLinkBadge, SoftoneLinkSelect } from "../../../components/admin/softone/SoftoneLink"

export default function AdminBuildingsPage() {
  const dispatch = useAppDispatch()
  const { buildings = [], isLoading } = useAppSelector((state) => state.buildings)
  const { isLoading: messagesLoading } = useAppSelector((state) => state.messages)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null)
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    zipCode: "",
    totalRooms: "",
  })

  const [showChatModal, setShowChatModal] = useState(false)
  const [selectedBuildingForChat, setSelectedBuildingForChat] = useState<any>(null)
  const [chatMessage, setChatMessage] = useState("")

  const [archiveConfirm, setArchiveConfirm] = useState<{
    show: boolean
    buildingId: string
    buildingName: string
    isArchiving: boolean
  }>({
    show: false,
    buildingId: "",
    buildingName: "",
    isArchiving: true,
  })

  // SoftOne links (admin only; loaded separately because SoftOne can be slow)
  const [softoneItems, setSoftoneItems] = useState<Record<string, SoftoneBuildingLinkItem>>({})
  const [softoneBuildings, setSoftoneBuildings] = useState<SoftoneBuilding[]>([])
  const [softoneLoading, setSoftoneLoading] = useState(false)
  const [softoneError, setSoftoneError] = useState<string | null>(null)
  const [approvingSoftoneBuildingId, setApprovingSoftoneBuildingId] = useState<string | null>(null)
  const [softoneValue, setSoftoneValue] = useState("")
  const [softoneInitialValue, setSoftoneInitialValue] = useState("")

  useEffect(() => {
    dispatch(fetchBuildings(false))
  }, [dispatch])

  const loadSoftoneLinks = useCallback(async () => {
    setSoftoneLoading(true)
    setSoftoneError(null)
    try {
      const data = await fetchSoftoneBuildingLinks()
      setSoftoneItems(Object.fromEntries(data.items.map((item) => [item.buildingId, item])))
      setSoftoneBuildings(data.softoneBuildings)
    } catch (error: any) {
      console.error("Error loading SoftOne links:", error)
      setSoftoneError(error.response?.data?.error || t("softoneLoadError"))
    } finally {
      setSoftoneLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSoftoneLinks()
  }, [loadSoftoneLinks])

  const handleApproveSoftoneSuggestion = async (buildingId: string, softonePrjc: string) => {
    setApprovingSoftoneBuildingId(buildingId)
    try {
      const link = await saveSoftoneBuildingLink(buildingId, { status: "linked", softonePrjc })
      setSoftoneItems((prev) => ({ ...prev, [buildingId]: { ...prev[buildingId], link, missing: false } }))
      toast({ title: t("success"), description: t("softoneLinkSaved") })
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.response?.data?.error || t("softoneLinkSaveError"),
        variant: "destructive",
      })
    } finally {
      setApprovingSoftoneBuildingId(null)
    }
  }

  const softoneBuildingOptions = softoneBuildings.map((b) => ({
    value: b.prjc,
    label: `${b.code} — ${b.name} (${b.address}, ${b.city})`,
  }))

  const handleCreateBuildingChat = async () => {
    if (!selectedBuildingForChat?.id || !chatMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message for the building chat",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        createBuildingChat({
          buildingId: selectedBuildingForChat.id,
          message: chatMessage,
        }),
      ).unwrap()

      if (result.success) {
        toast({
          title: "Success",
          description: `Building chat created for ${selectedBuildingForChat.name}`,
        })
        setShowChatModal(false)
        setChatMessage("")
        setSelectedBuildingForChat(null)
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error || "Failed to create building chat",
        variant: "destructive",
      })
    }
  }

  const handleOpenChatModal = (building: any) => {
    setSelectedBuildingForChat(building)
    setChatMessage(
      `Welcome to the ${building.name} building chat! This is a space for all residents to communicate and stay connected.`,
    )
    setShowChatModal(true)
  }

  const handleCreateBuilding = async () => {
    if (!formData.name || !formData.address || !formData.city || !formData.zipCode || !formData.totalRooms) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        addBuilding({
          name: formData.name,
          address: formData.address,
          city: formData.city,
          zipCode: formData.zipCode,
          totalRooms: Number(formData.totalRooms),
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Building added successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add building",
        variant: "destructive",
      })
    }
  }

  const handleUpdateBuilding = async () => {
    if (!selectedBuilding?.id) {
      toast({
        title: "Error",
        description: "No building selected for editing",
        variant: "destructive",
      })
      return
    }

    if (!formData.name || !formData.address || !formData.city || !formData.zipCode || !formData.totalRooms) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        updateBuilding({
          id: selectedBuilding.id,
          name: formData.name,
          address: formData.address,
          city: formData.city,
          zipCode: formData.zipCode,
          totalRooms: Number(formData.totalRooms),
        }),
      ).unwrap()

      if (softoneValue !== softoneInitialValue) {
        try {
          await saveSoftoneBuildingLink(selectedBuilding.id, selectValueToChoice(softoneValue))
          loadSoftoneLinks()
        } catch (error: any) {
          toast({
            title: "Error",
            description: error.response?.data?.error || t("softoneLinkSaveError"),
            variant: "destructive",
          })
          return
        }
      }

      toast({
        title: "Success",
        description: "Building updated successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      console.error("Update building error:", error)
      toast({
        title: "Error",
        description: "Failed to update building",
        variant: "destructive",
      })
    }
  }

  const handleArchiveBuilding = async (id: string, name: string) => {
    setArchiveConfirm({
      show: true,
      buildingId: id,
      buildingName: name,
      isArchiving: true,
    })
  }

  const handleUnarchiveBuilding = async (id: string, name: string) => {
    setArchiveConfirm({
      show: true,
      buildingId: id,
      buildingName: name,
      isArchiving: false,
    })
  }

  const confirmArchiveAction = async () => {
    try {
      if (archiveConfirm.isArchiving) {
        await dispatch(archiveBuilding(archiveConfirm.buildingId)).unwrap()
        toast({
          title: "Success",
          description: "Building archived successfully",
        })
      } else {
        await dispatch(unarchiveBuilding(archiveConfirm.buildingId)).unwrap()
        toast({
          title: "Success",
          description: "Building unarchived successfully",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${archiveConfirm.isArchiving ? "archive" : "unarchive"} building`,
        variant: "destructive",
      })
    } finally {
      setArchiveConfirm({ show: false, buildingId: "", buildingName: "", isArchiving: true })
    }
  }

  const handleEditBuilding = (building: any) => {
    setSelectedBuilding(building)
    setFormData({
      name: building.name,
      address: building.address,
      city: building.city,
      zipCode: building.zipCode,
      totalRooms: building.totalRooms.toString(),
    })
    const softoneSelectValue = linkToSelectValue(softoneItems[building.id]?.link)
    setSoftoneValue(softoneSelectValue)
    setSoftoneInitialValue(softoneSelectValue)
    setIsEditing(true)
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      city: "",
      zipCode: "",
      totalRooms: "",
    })
    setIsEditing(false)
    setSelectedBuilding(null)
  }

  const filteredBuildings = buildings
    ? buildings.filter(
        (building) =>
          building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          building.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          building.city.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("buildingManagement")}</h1>
        <PlusButton
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          title={t("addBuilding")}
        />
      </div>

      <Card className="mb-4 border-0 shadow-sm mt-4">
        <Card.Body className="p-4">
          <div className="mb-3">
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={t("searchBuildings")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5"
              />
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
            </div>
          </div>

          {softoneError && (
            <Alert variant="warning" className="py-2">
              {t("softoneLoadError")}: {softoneError}
            </Alert>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("name")}</th>
                    <th className="fw-semibold text-gray-700">{t("address")}</th>
                    <th className="fw-semibold text-gray-700">{t("city")}</th>
                    <th className="fw-semibold text-gray-700">{t("zipCode")}</th>
                    <th className="fw-semibold text-gray-700">{t("totalRooms")}</th>
                    <th className="fw-semibold text-gray-700">{t("softoneColumn")}</th>
                    <th className="fw-semibold text-gray-700">{t("status")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBuildings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        {t("noBuildingsFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredBuildings.map((building) => (
                      <tr key={building.id} className="border-bottom">
                        <td className="align-middle">{building.name}</td>
                        <td className="align-middle">{building.address}</td>
                        <td className="align-middle">{building.city}</td>
                        <td className="align-middle">{building.zipCode}</td>
                        <td className="align-middle">{building.totalRooms}</td>
                        <td className="align-middle">
                          {softoneLoading && !softoneItems[building.id] ? (
                            <Spinner animation="border" size="sm" variant="secondary" />
                          ) : (
                            <SoftoneLinkBadge
                              link={softoneItems[building.id]?.link}
                              suggestion={softoneItems[building.id]?.suggestion}
                              note={softoneItems[building.id]?.note}
                              warning={softoneItems[building.id]?.missing ? t("softoneMissing") : undefined}
                              approving={approvingSoftoneBuildingId === building.id}
                              onApproveSuggestion={
                                softoneItems[building.id]?.suggestion
                                  ? () =>
                                      handleApproveSoftoneSuggestion(
                                        building.id,
                                        softoneItems[building.id]!.suggestion!.softonePrjc,
                                      )
                                  : undefined
                              }
                            />
                          )}
                        </td>
                        <td className="align-middle">
                          <span className={`badge ${building.isArchived ? "bg-secondary" : "bg-success"}`}>
                            {building.isArchived ? t("archived") : t("active")}
                          </span>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            {!building.isArchived && (
                              <Button
                                className="admin-toggle-btn"
                                size="sm"
                                onClick={() => handleOpenChatModal(building)}
                                title={`Create building chat for ${building.name}`}
                                disabled={messagesLoading}
                              >
                                <Users size={16} />
                              </Button>
                            )}
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditBuilding(building)}
                              title={t("editBuilding")}
                            >
                              <Edit size={16} />
                            </Button>
                            {building.isArchived ? (
                              <Button
                                className="admin-toggle-btn"
                                size="sm"
                                onClick={() => handleUnarchiveBuilding(building.id, building.name)}
                                title={t("unarchiveBuilding")}
                              >
                                <RefreshCw size={16} />
                              </Button>
                            ) : (
                              <Button
                                className="admin-delete-btn"
                                size="sm"
                                onClick={() => handleArchiveBuilding(building.id, building.name)}
                                title={t("archiveBuilding")}
                              >
                                <Archive size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showChatModal}
        onHide={() => {
          setShowChatModal(false)
          setChatMessage("")
          setSelectedBuildingForChat(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Users className="me-2" size={20} />
            Create Building Chat - {selectedBuildingForChat?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Initial Welcome Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Enter a welcome message for the building chat..."
              />
              <Form.Text className="text-muted">
                This message will be the first message in the building-wide chat for all residents.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowChatModal(false)
              setChatMessage("")
              setSelectedBuildingForChat(null)
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateBuildingChat}
            disabled={messagesLoading || !chatMessage.trim()}
          >
            {messagesLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Creating...
              </>
            ) : (
              <>
                <Users className="me-2" size={16} />
                Create Building Chat
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create/Edit Building Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          resetForm()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? t("editBuilding") : t("addBuilding")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("name")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("enterBuildingName")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("address")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder={t("enterAddress")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("city")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder={t("enterCity")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("zipCode")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                placeholder={t("enterZipCode")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("totalRooms")}</Form.Label>
              <Form.Control
                type="number"
                value={formData.totalRooms}
                onChange={(e) => setFormData({ ...formData, totalRooms: e.target.value })}
                placeholder={t("enterTotalRooms")}
              />
            </Form.Group>
            {isEditing && selectedBuilding && (
              <SoftoneLinkSelect
                value={softoneValue}
                onChange={setSoftoneValue}
                options={softoneBuildingOptions}
                suggestion={softoneItems[selectedBuilding.id]?.suggestion}
                note={softoneItems[selectedBuilding.id]?.note}
                warning={softoneItems[selectedBuilding.id]?.missing ? t("softoneMissing") : undefined}
                loading={softoneLoading}
                disabled={!!softoneError}
              />
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false)
              resetForm()
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={isEditing ? handleUpdateBuilding : handleCreateBuilding}
            disabled={isLoading}
          >
            {isEditing ? t("update") : t("create")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        show={archiveConfirm.show}
        onHide={() => setArchiveConfirm({ show: false, buildingId: "", buildingName: "", isArchiving: true })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{archiveConfirm.isArchiving ? t("confirmArchive") : t("confirmUnarchive")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {archiveConfirm.isArchiving
              ? t("archiveBuildingConfirmation", { name: archiveConfirm.buildingName })
              : t("unarchiveBuildingConfirmation", { name: archiveConfirm.buildingName })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setArchiveConfirm({ show: false, buildingId: "", buildingName: "", isArchiving: true })}
          >
            {t("cancel")}
          </Button>
          <Button variant={archiveConfirm.isArchiving ? "secondary" : "success"} onClick={confirmArchiveAction}>
            {archiveConfirm.isArchiving ? t("archive") : t("unarchive")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
